import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const formSchema = z.object({
  state: z.string().min(1, { message: 'State is required' }),
  jurisdiction: z.string().optional(),
  last_name: z.string().min(1, { message: 'Last name is required' }),
  incident_date: z.string().min(1, { message: 'Incident date is required' }),
  report_id: z.string().optional(), // Made optional since it might not be known yet
});

type FormValues = z.infer<typeof formSchema>;

export interface CrashReportLookupInitialData {
  state?: string;
  jurisdiction?: string;
  last_name?: string;
  incident_date?: string;
  report_id?: string;
}

interface CrashReportLookupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormValues) => void;
  isLoading?: boolean;
  initialData?: CrashReportLookupInitialData;
}

// Custom styled input with bottom border only
const UnderlinedInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { isError?: boolean }
>(({ className, isError, ...props }, ref) => (
  <input
    className={`w-full bg-transparent border-0 border-b border-gray-300 focus:border-primary focus:outline-none focus:ring-0 rounded-none px-0 py-1 ${
      isError ? 'border-red-500' : ''
    } ${className}`}
    ref={ref}
    {...props}
  />
));
UnderlinedInput.displayName = 'UnderlinedInput';

export const CrashReportLookupModal: React.FC<CrashReportLookupModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  initialData,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      state: initialData?.state || '',
      jurisdiction: initialData?.jurisdiction || '',
      last_name: initialData?.last_name || '',
      incident_date: initialData?.incident_date || '',
      report_id: initialData?.report_id || '',
    },
  });

  React.useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  // Update form when initialData changes
  React.useEffect(() => {
    if (isOpen && initialData) {
      reset({
        state: initialData.state || '',
        jurisdiction: initialData.jurisdiction || '',
        last_name: initialData.last_name || '',
        incident_date: initialData.incident_date || '',
        report_id: initialData.report_id || '',
      });
    }
  }, [isOpen, initialData, reset]);

  const onFormSubmit = (data: FormValues) => {
    onSubmit(data);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle className="text-3xl font-normal mb-1">
            Police Report Lookup
          </DialogTitle>
          {initialData &&
            (initialData.state ||
              initialData.jurisdiction ||
              initialData.incident_date ||
              initialData.report_id) && (
              <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded-md">
                📋 Form prefilled with data from attached police report. Please
                review and update any fields as needed.
              </div>
            )}
        </DialogHeader>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="uppercase text-sm text-muted-foreground font-medium mb-1">
                  STATE <span className="text-red-500">*</span>
                </p>
                <UnderlinedInput
                  id="state"
                  placeholder="TX - Texas"
                  {...register('state')}
                  isError={!!errors.state}
                />
                {errors.state && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.state.message}
                  </p>
                )}
              </div>

              <div>
                <p className="uppercase text-sm text-muted-foreground font-medium mb-1">
                  JURISDICTION
                </p>
                <UnderlinedInput
                  id="jurisdiction"
                  placeholder="Hurst Police Department"
                  {...register('jurisdiction')}
                  isError={!!errors.jurisdiction}
                />
                {errors.jurisdiction && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.jurisdiction.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <p className="uppercase text-sm text-muted-foreground font-medium mb-1">
                  LAST NAME <span className="text-red-500">*</span>
                </p>
                <UnderlinedInput
                  id="last_name"
                  placeholder="Smith"
                  {...register('last_name')}
                  isError={!!errors.last_name}
                />
                {errors.last_name && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.last_name.message}
                  </p>
                )}
              </div>

              <div>
                <p className="uppercase text-sm text-muted-foreground font-medium mb-1">
                  INCIDENT DATE <span className="text-red-500">*</span>
                </p>
                <UnderlinedInput
                  id="incident_date"
                  type="date"
                  {...register('incident_date')}
                  isError={!!errors.incident_date}
                  className="text-muted-foreground placeholder:text-muted-foreground"
                  placeholder="mm/dd/yyyy"
                />
                {errors.incident_date && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.incident_date.message}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-3">
              <p className="uppercase text-sm text-muted-foreground font-medium mb-1">
                REPORT ID{' '}
                <span className="text-xs text-muted-foreground lowercase">
                  (optional)
                </span>
              </p>
              <UnderlinedInput
                id="report_id"
                placeholder="Enter report number"
                {...register('report_id')}
                isError={!!errors.report_id}
              />
              {errors.report_id && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.report_id.message}
                </p>
              )}
            </div>
          </div>

          <div className="mt-8">
            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
